/**
 * Champ de saisie étiqueté.
 *
 * L’étiquette est toujours visible (jamais un simple `placeholder`) et les messages d’erreur
 * sont reliés au champ par `aria-describedby` : un lecteur d’écran annonce l’erreur au bon
 * endroit. Le texte du message est déjà traduit par l’appelant.
 */
import type { InputHTMLAttributes } from 'react';

export interface ProprietesChamp extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  readonly id: string;
  readonly etiquette: string;
  /** Consigne permanente (règle de saisie), affichée sous le champ. */
  readonly aide?: string;
  /** Message d’erreur traduit, ou `null` s’il n’y en a pas. */
  readonly erreur?: string | null;
}

export function Champ({ id, etiquette, aide, erreur, className, ...reste }: ProprietesChamp) {
  const idAide = aide === undefined ? null : `${id}-aide`;
  const idErreur = erreur === null || erreur === undefined ? null : `${id}-erreur`;
  const decrit = [idAide, idErreur].filter((valeur): valeur is string => valeur !== null);
  const classes = ['champ', className].filter(Boolean).join(' ');
  return (
    <div className={classes}>
      <label className="champ__etiquette" htmlFor={id}>
        {etiquette}
      </label>
      <input
        {...reste}
        id={id}
        className="champ__saisie"
        aria-invalid={idErreur === null ? undefined : true}
        aria-describedby={decrit.length === 0 ? undefined : decrit.join(' ')}
      />
      {idAide === null ? null : (
        <p className="champ__aide" id={idAide}>
          {aide}
        </p>
      )}
      {idErreur === null ? null : (
        <p className="champ__erreur" id={idErreur} role="alert">
          {erreur}
        </p>
      )}
    </div>
  );
}
